'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const challenges = [
  {
    tag: '[01]',
    title: 'PCI-DSS & Open Banking Compliance',
    desc: 'PSD2, Open Banking APIs, and PCI-DSS v4.0 create a dense compliance landscape. FinTechs must demonstrate security maturity to regulators, card schemes, and banking partners simultaneously.',
  },
  {
    tag: '[02]',
    title: 'Rapid Product Velocity vs. Security Debt',
    desc: 'FinTech growth depends on shipping fast. Security is often bolted on after the fact, leaving payment flows, customer data, and fraud controls exposed as the product scales.',
  },
  {
    tag: '[03]',
    title: 'API Security at Scale',
    desc: 'Open Banking and embedded finance architectures expose hundreds of API endpoints. Business-logic vulnerabilities in payment and account APIs are not caught by standard scanners.',
  },
  {
    tag: '[04]',
    title: 'Investor & Partner Due Diligence',
    desc: 'Series A and beyond, banking partner onboarding, and card scheme certification all require demonstrable security posture, often on short timelines with limited internal resource.',
  },
];

const services = [
  { label: 'PCI-DSS Continuous Compliance', href: '/services/compliance', desc: 'Continuous control monitoring, evidence collection, and cardholder data environment security, replacing annual point-in-time assessments with ongoing assurance.' },
  { label: 'AppSec for Payment Applications', href: '/services/appsec', desc: 'SAST, DAST, and SCA embedded into your CI/CD pipelines. Business-logic vulnerabilities in payment flows and account APIs that automated scanners miss.' },
  { label: 'Accelerator Programme', href: '/services/accelerator', desc: 'Rapid security readiness for investor due diligence, card scheme certification, or banking partner onboarding. Senior engineers embedded, doing the work at pace.' },
  { label: 'Threat Modelling for FinTech', href: '/services/threat-modelling', desc: 'STRIDE applied to payment flows, fraud controls, and Open Banking APIs. Architecture-level risk identification before vulnerabilities reach production.' },
  { label: 'Penetration Testing', href: '/services/pentesting', desc: 'Web, API, and mobile penetration testing across payment platforms and customer-facing applications, with FinTech-specific threat context and business-logic focus.' },
  { label: 'Security Focus Programme', href: '/services/security-focus', desc: 'Auditor-aligned prioritisation that cuts unnecessary work. Focus effort on the controls that close real exposure and satisfy your next compliance milestone.' },
];

const outcomes = [
  { metric: 'PCI-DSS', label: 'v4.0 compliance achieved and maintained continuously' },
  { metric: 'PSD2', label: 'Open Banking API security controls evidenced for regulators' },
  { metric: 'SOC 2', label: 'Type II certification alongside regulatory compliance' },
  { metric: 'Series B+', label: 'Security posture ready for investor and partner due diligence' },
];

export default function FintechPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">FinTech & Payments</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [FinTech & Payments]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security that moves<br />
              <span className="text-[#80edfa]">as fast as your product.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              FinTechs face PCI-DSS, PSD2, and investor due diligence on timelines that don't allow for slow security programmes. TechSonar embeds senior security engineers to build continuous compliance, secure payment flows, and satisfy card schemes and regulators, without slowing your product velocity.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes FinTech security hard.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services tailored to FinTech and payments.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Ready to close the gap before your next compliance milestone?
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you are preparing for PCI-DSS certification, a card scheme audit, or investor due diligence, TechSonar can get you there. Senior engineers, embedded, doing the work.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/accelerator" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View Accelerator Programme
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
